import { createSelector } from '@ngrx/store';

import * as subscriptionHelpers from 'src/app/modules/dashboard/helpers/subscriptions.helpers';
import { selectDashboardState } from '../dashboard.selectors';

const selectSubscriptionsState = createSelector(
  selectDashboardState,
  (dashboardState) => dashboardState.subscriptions
);

export const selectActiveSubscription = createSelector(
  selectSubscriptionsState,
  (subscriptionsState) => subscriptionsState.activeSubscription
);

export const selectActiveSubscriptionId = createSelector(
  selectActiveSubscription,
  (activeSubscription) => (!!activeSubscription ? activeSubscription.id : null)
);

export const selectActiveSubscriptionRenewalDate = createSelector(
  selectActiveSubscription,
  (activeSubscription) =>
    subscriptionHelpers.getSubscriptionRenewalDate(activeSubscription)
);

export const selectIsActiveSubscriptionAutoRenewCancelled = createSelector(
  selectActiveSubscription,
  (activeSubscription) => {
    return subscriptionHelpers.getIsSubscriptionAutoRenewCancelled(
      activeSubscription
    );
  }
);

export const selectIsFamilyOptionAutoRenewCancelled = createSelector(
  selectActiveSubscription,
  (activeSubscription) => {
    return (
      !!activeSubscription.familyOption &&
      !activeSubscription.familyOption.isAutoRenewed
    );
  }
);

export const selectIsFamilyOptionAutoRenewCancelledOnly = createSelector(
  selectActiveSubscription,
  (activeSubscription) => {
    return subscriptionHelpers.getIsFamilyOptionAutoRenewCancelledOnly(
      activeSubscription
    );
  }
);

export const selectDoesActiveSubscriptionHaveFamilyOption = createSelector(
  selectActiveSubscription,
  (activeSubscription) => !!activeSubscription?.familyOption
);

export const selectFamilyOption = createSelector(
  selectActiveSubscription,
  (activeSubscription) => {
    if (!activeSubscription) {
      return null;
    }
    return activeSubscription.familyOption;
  }
);

export const selectFamilyOptionId = createSelector(
  selectFamilyOption,
  (familyOption) => {
    if (!familyOption) {
      return null;
    }
    return familyOption.id;
  }
);

export const selectFamilyOptionEndDate = createSelector(
  selectFamilyOption,
  (familyOption) => {
    if (!familyOption) {
      return null;
    }
    return familyOption.endDate;
  }
);

export const selectIsFamilyOptionAutoRenewed = createSelector(
  selectFamilyOption,
  (familyOption) => familyOption?.isAutoRenewed
);

export const selectNumberOfUserAcounts = createSelector(
  selectFamilyOption,
  (familyOption) => (!!familyOption ? familyOption.members.length + 1 : 0)
);

export const selectIsLoadingActiveSubscription = createSelector(
  selectSubscriptionsState,
  (subscriptionsState) => subscriptionsState.isLoadingActiveSubscription
);

export const selectSortedFormerSubscriptions = createSelector(
  selectSubscriptionsState,
  (subscriptionsState) => {
    return [...subscriptionsState.formerSubscriptions]
      .filter((subscription) => {
        return subscription.id !== subscriptionsState.activeSubscription?.id;
      })
      .sort((s1, s2) => {
        return s1.endDate > s2.endDate ? -1 : 1;
      });
  }
);

export const selectIsLoadingFormerSubscriptions = createSelector(
  selectSubscriptionsState,
  (subscriptionsState) => subscriptionsState.isLoadingFormerSubscriptions
);

export const selectSubscriptionById = (subscriptionId: number) => {
  return createSelector(selectSubscriptionsState, (subscriptionsState) => {
    const subscription = [
      subscriptionsState.activeSubscription,
      ...subscriptionsState.formerSubscriptions,
    ].find((subscription) => subscription?.id === subscriptionId);
    if (!subscription) {
      return null;
    }
    return subscription;
  });
};
